import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import useReveal from '../utils/useReveal';
import './Home.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const IconMision = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4.5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <line x1="12" y1="1.5" x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22.5" />
    <line x1="1.5" y1="12" x2="5" y2="12" />
    <line x1="19" y1="12" x2="22.5" y2="12" />
  </svg>
);

const IconVision = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconoWhatsApp = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.28-1.38a9.9 9.9 0 0 0 4.71 1.2h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.13-2.9-7C17.18 3.03 14.69 2 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.13.82.84-3.05-.2-.31a8.22 8.22 0 0 1-1.26-4.39c0-4.55 3.7-8.25 8.26-8.25 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.84c0 4.55-3.71 8.25-8.25 8.25Zm4.52-6.18c-.25-.12-1.47-.72-1.69-.81-.23-.08-.4-.12-.56.13-.17.25-.64.81-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.24-.41.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43-.14-.01-.31-.01-.48-.01-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.6.12.17 1.78 2.71 4.3 3.81.6.26 1.07.42 1.44.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.28Z" />
  </svg>
);

// Fotos reales de trabajos en terreno para la galería del Home
const galeriaTrabajos = [
  {
    src: '/imagenes/obras-civiles-instalacion.webp',
    alt: 'Obras civiles: instalación de romana de camiones',
    caption: 'Obras Civiles y Montaje',
  },
  {
    src: '/imagenes/calibracion-servicio-tecnico.webp',
    alt: 'Servicio técnico: calibración con masas patrón',
    caption: 'Servicio Técnico',
  },
  {
    src: '/imagenes/fundacion-bascula-camiones.webp',
    alt: 'Construcción de fundación para báscula de camiones',
    caption: 'Fundación de Báscula',
  },
  {
    src: '/imagenes/panel-control-automatizacion.webp',
    alt: 'Panel de control de proceso automatizado',
    caption: 'Automatización de Procesos',
  },
  {
    src: '/imagenes/calibracion-pesas-patron.webp',
    alt: 'Verificación de báscula de camiones en terreno',
    caption: 'Verificación en Terreno',
  },
  {
    src: '/imagenes/planta-industrial.webp',
    alt: 'Mantención en planta de proceso industrial',
    caption: 'Mantención en Planta',
  },
  {
    src: '/imagenes/masas-patron-calibracion.webp',
    alt: 'Calibración con masas patrón certificadas',
    caption: 'Masas Patrón Certificadas',
  },
  {
    src: '/imagenes/mantenimiento-celda-carga.webp',
    alt: 'Mantenimiento de celda de carga',
    caption: 'Mantenimiento de Celdas de Carga',
  },
];

const Home = () => {
  useReveal();

  // El video de fondo solo se monta (y se descarga) en pantallas grandes
  // sin preferencia de movimiento reducido; en móvil se queda con la foto fija.
  const [mostrarVideo, setMostrarVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const query = window.matchMedia('(min-width: 769px) and (prefers-reduced-motion: no-preference)');
    setMostrarVideo(query.matches);
    const onChange = (event) => setMostrarVideo(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Pausar el video al salir del viewport (scroll) para ahorrar CPU/batería,
  // y reanudarlo al volver a verse.
  useEffect(() => {
    const video = videoRef.current;
    if (!mostrarVideo || !video || !('IntersectionObserver' in window)) return undefined;

    let visible = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0 }
    );
    observer.observe(video);

    // Algunos navegadores dejan el video pausado justo en el punto de loop;
    // si sigue visible, lo retomamos.
    const onPause = () => {
      if (visible) video.play().catch(() => {});
    };
    video.addEventListener('pause', onPause);

    return () => {
      observer.disconnect();
      video.removeEventListener('pause', onPause);
    };
  }, [mostrarVideo]);

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        {mostrarVideo && (
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster="/imagenes/bascula-camiones-hero.webp"
          >
            <source src="/videos/hero-bascula-camiones.webm" type="video/webm" />
            <source src="/videos/hero-bascula-camiones.mp4" type="video/mp4" />
          </video>
        )}
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <p className="eyebrow hero-eyebrow" data-reveal>Pesaje industrial · Chile</p>
              <h1 className="hero-title" aria-label="Servibalanzas">
                {'Servibalanzas'.split('').map((letra, index) => (
                  <span
                    key={index}
                    className="hero-letra"
                    aria-hidden="true"
                    style={{ '--letra-delay': `${0.25 + index * 0.09}s` }}
                  >
                    {letra}
                  </span>
                ))}
              </h1>
              <p className="hero-subtitle" data-reveal style={{ '--reveal-delay': '1.4s' }}>
                En Servibalanzas, nos especializamos en ofrecer un servicio técnico integral
                para una amplia gama de equipos de pesaje,
                desde balanzas de precisión hasta equipos de alto tonelaje utilizados en aplicaciones industriales.
              </p>
              <div className="hero-buttons" data-reveal style={{ '--reveal-delay': '1.7s' }}>
                <Link to="/servicios" className="btn btn-primary">
                  Nuestros Servicios
                </Link>
                <Link to="/productos" className="btn btn-secondary">
                  Productos
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-stats" data-reveal style={{ '--reveal-delay': '2s' }}>
          <div className="container hero-stats-grid">
            <div className="hero-stat">
              <span className="hero-stat-value">Equipo Especializado</span>
              <span className="hero-stat-label">Técnicos con experiencia en pesaje</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-value">1 mg — 100 t</span>
              <span className="hero-stat-label">rango de pesaje</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-value">Servicio Técnico</span>
              <span className="hero-stat-label">Atención directa y personalizada</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Cotización por WhatsApp */}
      <section className="cta-banner">
        <div className="container cta-banner-inner">
          <p className="eyebrow" data-reveal>Cotización rápida</p>
          <h2 className="cta-title" data-reveal style={{ '--reveal-delay': '0.1s' }}>
            Sistemas de Pesaje para tu Industria
          </h2>
          <p className="cta-subtitle" data-reveal style={{ '--reveal-delay': '0.2s' }}>
            Venta de equipos, calibración trazable INN con camión patrón,
            mantenimiento, automatización y fabricación de plataformas
            especiales para mineria, alimentos y manofactura en Chile.
          </p>
          <a
            href="https://wa.me/+56976234326?text=Hola,%20quiero%20cotizar%20un%20sistema%20de%20pesaje%20industrial"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-whatsapp-btn"
            data-reveal
            style={{ '--reveal-delay': '0.3s' }}
          >
            <IconoWhatsApp />
            Cotizar Servicio
          </a>
        </div>
      </section>

      {/* Quiénes somos + Misión / Visión */}
      <section className="section about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-text">
              <p className="eyebrow" data-reveal>La empresa</p>
              <h2 className="about-title" data-reveal style={{ '--reveal-delay': '0.1s' }}>¿Quiénes somos?</h2>
              <p className="about-lead" data-reveal style={{ '--reveal-delay': '0.2s' }}>
                Empresa con más de 30 años de experiencia en el mercado, brindamos soluciones
                inteligentes de pesaje, desde 1 miligramo hasta más de 100 toneladas,
                garantizando disponibilidad y precisión en las mediciones de los equipos
                que comercializamos.
              </p>
            </div>
            <div className="about-cards">
              <div className="about-card" data-reveal style={{ '--reveal-delay': '0.15s' }}>
                <span className="about-card-icon"><IconMision /></span>
                <h3>Misión</h3>
                <p>Ofrecemos sistemas inteligentes de pesaje y sus servicios complementarios, para que nuestros clientes cuenten con un sistema que les permita optimizar sus procesos y aumentar su productividad.</p>
              </div>
              <div className="about-card" data-reveal style={{ '--reveal-delay': '0.3s' }}>
                <span className="about-card-icon"><IconVision /></span>
                <h3>Visión</h3>
                <p>Ser la empresa líder en Chile en soluciones de pesaje industrial, reconocida por la calidad de sus productos, la rapidez en la respuesta y la cercanía con sus clientes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="tick-divider" aria-hidden="true" style={{ color: 'var(--primary-teal)' }} />

      {/* Productos y Servicios */}
      <section className="section divisions-section">
        <div className="container">
          <div className="divisions-header">
            <p className="eyebrow" data-reveal>Lo que hacemos</p>
            <h2 className="section-title" data-reveal style={{ '--reveal-delay': '0.1s' }}>Nuestros Productos y Servicios</h2>
            <p className="section-subtitle" data-reveal style={{ '--reveal-delay': '0.2s' }}>
              Descubra nuestra amplia gama de equipos de pesaje industrial y servicios
              especializados diseñados para optimizar sus procesos productivos
            </p>
          </div>

          <div className="divisions-carousel" data-reveal style={{ '--reveal-delay': '0.25s' }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              grabCursor={true}
              className="divisions-swiper"
            >
              {/* Mantenimiento */}
              <SwiperSlide>
                <div className="product-card">
                  <div className="product-image">
                    <img
                      src="/imagenes/calibracion-bascula-camiones.webp"
                      alt="Mantenimiento y calibración de báscula de camiones"
                    />
                    <div className="product-badge">Servicio</div>
                  </div>
                  <div className="product-content">
                    <h3>Mantenimiento</h3>
                    <p className="product-description">
                    El mantenimiento en el área del pesaje industrial es un servicio clave que se
                    realiza para asegurar que las balanzas, básculas,
                    celdas de carga y sistemas automatizados funcionen correctamente, con precisión
                    y sin interrupciones.
                    </p>
                    <div className="product-features">
                      <span className="feature-tag">Mantenimiento Preventivo</span>
                      <span className="feature-tag">Mantenimiento Correctivo</span>
                    </div>

                    <Link to="/servicios" className="product-btn">
                    Ver servicios
                    </Link>
                  </div>
                </div>
              </SwiperSlide>

              {/* Diagnóstico */}
              <SwiperSlide>
                <div className="product-card">
                  <div className="product-image">
                    <img
                      src="/imagenes/celda-carga-diagnostico.webp"
                      alt="Diagnóstico técnico de celda de carga"
                    />
                    <div className="product-badge">Servicio</div>
                  </div>
                  <div className="product-content">
                    <h3>Diagnóstico</h3>
                    <p className="product-description">
                    El diagnóstico consiste en una revisión técnica completa del equipo para detectar fallas, desgastes o desajustes que puedan afectar su funcionamiento.
                    Ideal para equipos que presentan errores, inestabilidad o caídas inesperadas.
                    </p>

                    <Link to="/servicios" className="product-btn">
                      Ver servicios
                    </Link>
                  </div>
                </div>
              </SwiperSlide>

              {/* Instalación y Montaje */}
              <SwiperSlide>
                <div className="product-card">
                  <div className="product-image">
                    <img
                      src="/imagenes/instalacion-plataforma.webp"
                      alt="Instalación de plataforma de pesaje"
                    />
                    <div className="product-badge">Servicio</div>
                  </div>
                  <div className="product-content">
                    <h3>Instalación y Montaje</h3>
                    <p className="product-description">
                    Instalación técnica y estructural de balanzas, básculas, celdas de carga y sistemas de pesaje en terreno.
                    </p>
                    <div className="product-features">
                      <span className="feature-tag">Certificados</span>
                      <span className="feature-tag">Profesionales</span>
                    </div>

                    <Link to="/servicios" className="product-btn">
                    Ver servicios
                    </Link>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>

            {/* Controles de navegación personalizados */}
            <div className="swiper-button-prev custom-nav-btn"></div>
            <div className="swiper-button-next custom-nav-btn"></div>
          </div>

        </div>
      </section>

      {/* Galería de Trabajos */}
      <section className="section galeria-section">
        <div className="container">
          <div className="divisions-header">
            <p className="eyebrow" data-reveal>Nuestro trabajo</p>
            <h2 className="section-title" data-reveal style={{ '--reveal-delay': '0.1s' }}>Galería de Trabajos</h2>
            <p className="section-subtitle" data-reveal style={{ '--reveal-delay': '0.2s' }}>
              Instalaciones, calibraciones y mantenciones realizadas en terreno
              por nuestro equipo técnico
            </p>
          </div>

          <div className="galeria-carousel" data-reveal style={{ '--reveal-delay': '0.25s' }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1.15}
              loop={true}
              navigation={{
                nextEl: '.galeria-button-next',
                prevEl: '.galeria-button-prev',
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              autoplay={{
                delay: 3500,
                disableOnInteraction: false,
              }}
              breakpoints={{
                640: { slidesPerView: 2.2, spaceBetween: 24 },
                992: { slidesPerView: 3.2, spaceBetween: 28 },
                1400: { slidesPerView: 4, spaceBetween: 28 },
              }}
              grabCursor={true}
              className="galeria-swiper"
            >
              {galeriaTrabajos.map((item) => (
                <SwiperSlide key={item.src}>
                  <figure className="galeria-item">
                    <img src={item.src} alt={item.alt} loading="lazy" />
                    <figcaption className="galeria-caption">{item.caption}</figcaption>
                  </figure>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Controles de navegación personalizados */}
            <div className="galeria-button-prev custom-nav-btn"></div>
            <div className="galeria-button-next custom-nav-btn"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
